/**
 * Staff directory — my staff + society-wide staff, calm rating-free cards.
 * Wired: GET /api/services/staff ({items: my flat's, directory: rest of the
 * society}), POST /staff (register new + link), POST /staff/:id/flats
 * (add an existing member to my flat). Port of screens-services.jsx StaffDir.
 */
import { router } from 'expo-router';
import React from 'react';
import { Image, View } from 'react-native';

import { space } from '@bero/tokens';
import { extractItems, str } from '@/components/gate/api';
import { InfoNote, Segmented } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT, type TFunc } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Field, IconButton, Input, Pill, Screen, SectionLabel, Sheet, Skeleton, TopBar } from '@/ui';

/** Arch-shaped avatar (design's signature motif) — alternates neutral/primary
 * tint by index so no single amber accent gets overused across a long list. */
function ArchAvatar({ name, photo, size = 48, tone = 'primary' }: {
  name: string; photo?: string; size?: number; tone?: 'primary' | 'neutral';
}) {
  const { colors } = useTheme();
  const initials = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const bg = tone === 'primary' ? colors.primary100 : colors.canvas2;
  const fg = tone === 'primary' ? colors.primary900 : colors.textSecondary;
  const shape = {
    width: size, height: size * 1.15,
    borderTopLeftRadius: size / 2, borderTopRightRadius: size / 2,
    borderBottomLeftRadius: size * 0.11, borderBottomRightRadius: size * 0.11,
    overflow: 'hidden' as const,
  };
  if (photo) {
    return <Image source={{ uri: photo }} style={[shape, { backgroundColor: colors.canvas2 }]} resizeMode="cover" />;
  }
  return (
    <View style={[shape, { backgroundColor: bg, alignItems: 'center', justifyContent: 'center' }]}>
      <AppText weight="700" style={{ color: fg, fontSize: size * 0.28, lineHeight: size * 0.36 }}>{initials}</AppText>
    </View>
  );
}

type StaffRow = {
  id: string; name: string; role: string; days: string;
  verified: boolean; inAt?: string; outAt?: string; photo?: string;
};

function toRow(s: unknown, i: number): StaffRow {
  return {
    id: str(s, 'id') ?? `s${i}`,
    photo: str(s, 'photo'),
    name: str(s, 'name', 'fullName') ?? 'Staff member',
    role: str(s, 'role') ?? 'Help',
    days: str(s, 'days', 'schedule') ?? '',
    verified: (s as { idVerified?: boolean })?.idVerified === true,
    inAt: str(s, 'todayInAt', 'inAt'),
    outAt: str(s, 'todayOutAt', 'outAt'),
  };
}

function presence(r: StaffRow): { key: DictKey; status: 'resolved' | 'pending' } {
  if (r.inAt && !r.outAt) return { key: 'staff_presence_inside', status: 'resolved' };
  if (r.outAt) return { key: 'staff_presence_left', status: 'pending' };
  return { key: 'staff_presence_not_in', status: 'pending' };
}

const ROLES = ['Maid', 'Cook', 'Driver', 'Nanny', 'Presswala', 'Other'];
const ROLE_KEY: Record<string, DictKey> = {
  Maid: 'staff_role_maid', Cook: 'staff_role_cook', Driver: 'staff_role_driver',
  Nanny: 'staff_role_nanny', Presswala: 'staff_role_presswala', Other: 'veh_other',
  Electrician: 'vendor_cat_electrician',
};
/** Localise a stored role (API sends it lowercased) — reactive via the hook t(). */
export function roleLabel(raw: string, t: TFunc): string {
  const cap = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  const k = ROLE_KEY[raw] ?? ROLE_KEY[cap];
  return k ? t(k) : raw;
}

export default function StaffDirScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [mine, setMine] = React.useState<StaffRow[]>([]);
  const [directory, setDirectory] = React.useState<StaffRow[]>([]);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [notice, setNotice] = React.useState<string | null>(null);
  const [linkingId, setLinkingId] = React.useState<string | null>(null);

  // add-new sheet
  const [addOpen, setAddOpen] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newRole, setNewRole] = React.useState('Maid');
  const [newPhone, setNewPhone] = React.useState('');
  const [newSchedule, setNewSchedule] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const body = await api.get<unknown>('/api/services/staff');
      setMine(extractItems<unknown>(body, 'items').map(toRow));
      const dir = (body && typeof body === 'object' && 'directory' in body)
        ? (body as { directory: unknown[] }).directory : [];
      setDirectory((Array.isArray(dir) ? dir : []).map(toRow));
      setState('ready');
    } catch {
      setState('error');
    }
  }, []);

  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  const linkExisting = async (r: StaffRow) => {
    setLinkingId(r.id);
    setNotice(null);
    try {
      await api.post<unknown>(`/api/services/staff/${encodeURIComponent(r.id)}/flats`, { action: 'add' });
      await load();
    } catch {
      setNotice(t('staff_add_error', { name: r.name }));
    } finally {
      setLinkingId(null);
    }
  };

  const createStaff = async () => {
    if (!newName.trim() || saving) return;
    setSaving(true);
    setNotice(null);
    try {
      await api.post<unknown>('/api/services/staff', {
        name: newName.trim(),
        role: newRole.toLowerCase(),
        phone: newPhone.trim() || undefined,
        schedule: newSchedule.trim() || undefined,
      });
      setAddOpen(false);
      setNewName(''); setNewRole('Maid'); setNewPhone(''); setNewSchedule('');
      await load();
    } catch {
      setNotice(t('staff_register_error'));
    } finally {
      setSaving(false);
    }
  };

  const renderCard = (r: StaffRow, i: number, inDirectory: boolean) => {
    const p = presence(r);
    return (
      <Card key={r.id} onPress={() => router.push(`/staff/${r.id}`)}>
        <View style={{ flexDirection: 'row', gap: space.s3, alignItems: 'center' }}>
          <ArchAvatar name={r.name} photo={r.photo} size={48} tone={i % 2 === 0 ? 'primary' : 'neutral'} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <AppText variant="headingSm" weight="500">{r.name}</AppText>
              {r.verified ? <Icon name="check-circle" size={14} color={colors.success500} /> : null}
            </View>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
              {[roleLabel(r.role, t), r.days].filter(Boolean).join(' · ')}
            </AppText>
          </View>
          {inDirectory ? (
            <Button
              title={linkingId === r.id ? t('staff_adding') : t('staff_add')}
              kind="secondary"
              onPress={() => void linkExisting(r)}
            />
          ) : (
            <Pill status={p.status} label={t(p.key)} />
          )}
        </View>
      </Card>
    );
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('staff_title')}
        onBack={() => router.back()}
        right={
          <IconButton label={t('staff_register_new')} onPress={() => setAddOpen(true)}>
            <Icon name="plus" size={22} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <InfoNote icon="info" style={{ marginBottom: space.s4 }}>
          {t('staff_info_note')}
        </InfoNote>

        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} h={80} />)}
          </View>
        ) : state === 'error' ? (
          <EmptyState
            icon={<Icon name="users" size={40} color={colors.textTertiary} />}
            title={t('staff_error_title')}
            body={t('staff_error_body')}
          />
        ) : (
          <>
            <SectionLabel>{t('staff_section_mine')}</SectionLabel>
            {mine.length ? (
              <View style={{ gap: space.s3 }}>
                {mine.map((r, i) => renderCard(r, i, false))}
              </View>
            ) : (
              <AppText variant="bodySm" tone="tertiary">
                {t('staff_empty_mine')}
              </AppText>
            )}

            {directory.length ? (
              <>
                <SectionLabel style={{ marginTop: space.s6 }}>{t('staff_section_directory')}</SectionLabel>
                <AppText variant="bodySm" tone="tertiary" style={{ marginTop: -4, marginBottom: space.s1 }}>
                  {t('staff_directory_hint')}
                </AppText>
                <View style={{ gap: space.s3 }}>
                  {directory.map((r, i) => renderCard(r, i, true))}
                </View>
              </>
            ) : null}

            {notice ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3 }}>{notice}</AppText> : null}
            <Button
              title={t('staff_view_attendance')}
              kind="secondary"
              style={{ marginTop: space.s4 }}
              onPress={() => router.push('/attendance')}
            />
          </>
        )}
      </Screen>

      <Sheet visible={addOpen} onClose={() => setAddOpen(false)}>
        <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s4 }}>{t('staff_sheet_title')}</AppText>
        <View style={{ gap: space.s4 }}>
          <Field label={t('staff_field_name')}>
            <Input value={newName} onChangeText={setNewName} placeholder={t('staff_name_ph')} />
          </Field>
          <Field label={t('staff_field_role')}>
            <Segmented options={ROLES} labels={ROLES.map((r) => t(ROLE_KEY[r]))} value={newRole} onChange={setNewRole} />
          </Field>
          <Field label={t('staff_field_phone')}>
            <Input value={newPhone} onChangeText={setNewPhone} placeholder="98XXXXXX21" keyboardType="phone-pad" />
          </Field>
          <Field label={t('staff_field_schedule')}>
            <Input value={newSchedule} onChangeText={setNewSchedule} placeholder={t('staff_schedule_ph')} />
          </Field>
        </View>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s3 }}>
          {t('staff_sheet_note')}
        </AppText>
        <Button
          title={t('staff_register')}
          size="lg"
          loading={saving}
          disabled={!newName.trim()}
          onPress={() => void createStaff()}
          style={{ marginTop: space.s4 }}
        />
      </Sheet>
    </Screen>
  );
}
